/**
 * @module Reducers.PromoOffer
 *
 */
import { PromoOffer } from 'app/models';
import { PromoOfferAction, ResolvePromoOfferParams } from 'app/actions/promoOffer';

export type PromoOfferState = {
  baseAmount: number | null,
  code: string | null,
  planCode: string | null,
  offer: PromoOffer | null,
  // The resolved trial length (backend-sourced, region-aware). Carried even for a
  // `none` offer so the pages don't re-derive the regional trial client-side.
  trialDays: number | null,
  // The currency the stored offer was resolved for. The slice is shared across
  // routes, so consumers gate freshness on it too — otherwise a resolution for
  // the same (code, plan) in another currency leaks its amount here.
  currency: string | null,
  pending: boolean,
  error: boolean,
  // The latest requested (code, plan, currency) — used to drop out-of-order
  // responses (a stale earlier resolve landing after a newer one).
  requested: ResolvePromoOfferParams | null,
};

const initialState: PromoOfferState = {
  baseAmount: null,
  code: null,
  planCode: null,
  offer: null,
  trialDays: null,
  currency: null,
  pending: false,
  error: false,
  requested: null,
};

// True when `request` is the resolve currently awaited — a stale response (from
// a superseded request) is ignored so it can't clobber the store.
function isLatest(request: ResolvePromoOfferParams, state: PromoOfferState): boolean {
  const latest = state.requested;
  return (
    !!latest &&
    (request.code || "") === (latest.code || "") &&
    request.planCode === latest.planCode &&
    request.currency === latest.currency
  );
}

export default function promoOfferReducer(state = initialState, action: PromoOfferAction): PromoOfferState {
  switch (action.type) {
    case '@promoOffer.resolve.pending':
      return { ...state, requested: action.payload, pending: true, error: false };
    case '@promoOffer.resolve.success': {
      // Ignore an out-of-order response for a superseded request.
      if (!isLatest(action.payload.request, state)) {
        return state;
      }
      // The endpoint returns a flat offer that always carries the display context
      // (code / planCode / baseAmount). Split it into the pieces the pages read;
      // a `none`-kind offer means no promo applied, so store offer: null (pages
      // treat a null offer as "no promo").
      const offer = action.payload.offer;
      return {
        ...state,
        baseAmount: offer.baseAmount,
        code: offer.code,
        planCode: offer.planCode,
        offer: offer.kind === 'none' ? null : offer,
        trialDays: offer.trialDays,
        currency: action.payload.request.currency || 'usd',
        pending: false,
        error: false,
      };
    }
    // A transport failure: for the latest request only, drop any stale envelope
    // and flag the error so the page can show a retry instead of a perpetual
    // loader; the echoed (code, planCode) clears the pages' freshness check.
    case '@promoOffer.resolve.failed':
      if (!isLatest(action.payload, state)) {
        return state;
      }
      return {
        ...state,
        baseAmount: null,
        code: action.payload.code,
        planCode: action.payload.planCode,
        offer: null,
        trialDays: null,
        currency: action.payload.currency || 'usd',
        pending: false,
        error: true,
      };
    default:
      return state;
  }
}
